// Client-side entry point for the multiplayer endless runner game
// Requirements: 1.1, 1.2, 2.1, 2.3, 7.4, 7.5

import { GameClient, GameClientCallbacks } from './game-client';
import { InputHandler } from './input-handler';
import { GameRenderer } from './game-renderer';
import { UIManager, UIManagerCallbacks, PlayerInfo } from './ui-manager';
import { StateSnapshot, InputEvent } from '../shared/types';
import { parseRequestPath } from '../shared/utils';

console.log('Multiplayer Endless Runner - Client starting...');

class GameApp {
  private gameClient: GameClient | null = null;
  private inputHandler: InputHandler | null = null;
  private gameRenderer: GameRenderer | null = null;
  private uiManager: UIManager;
  private isGameActive: boolean = false;
  private currentLobbyId: string | null = null;

  constructor() {
    // Initialize UI manager with callbacks
    const uiCallbacks: UIManagerCallbacks = {
      onCreateGame: this.handleCreateGame.bind(this),
      onJoinLobby: this.handleJoinLobby.bind(this),
      onStartGame: this.handleStartGame.bind(this),
      onPlayAgain: this.handlePlayAgain.bind(this),
      onLeaveLobby: this.handleLeaveLobby.bind(this)
    };

    this.uiManager = new UIManager(uiCallbacks);
  }

  /**
   * Initializes the game application based on URL
   */
  async initialize(): Promise<void> {
    try {
      // Check for URL parameters first (for direct game joining)
      const urlParams = new URLSearchParams(window.location.search);
      const lobbyId = urlParams.get('lobbyId');
      const playerId = urlParams.get('playerId');
      const pseudonym = urlParams.get('pseudonym');
      
      if (lobbyId && playerId && pseudonym) {
        // Direct join from lobby page - auto-connect to game
        console.log('Auto-joining lobby:', lobbyId, 'as', pseudonym);
        this.currentLobbyId = lobbyId;
        
        // Initialize game client
        this.initializeGameClient();
        
        // Connect to lobby and show waiting screen
        this.uiManager.showLobbyWaiting();
        this.uiManager.showStatus('Connecting to lobby...', 'info');
        
        try {
          await this.gameClient!.connect(lobbyId);
          this.uiManager.showStatus('Connected! Waiting for game to start...', 'success');
        } catch (error) {
          console.error('Failed to connect to lobby:', error);
          this.uiManager.showError('Failed to connect to lobby');
        }
        
        return;
      }
      
      // Parse the current URL to determine what to show
      const path = parseRequestPath(window.location.pathname);
      
      if (path.action === 'homepage') {
        // Show homepage - Requirements: 1.1
        this.uiManager.showHomepage();
      } else if (path.action === 'join-lobby' && path.lobbyId) {
        // Show join lobby screen - Requirements: 2.1
        this.currentLobbyId = path.lobbyId;
        this.uiManager.showJoinLobby();
      } else {
        // Invalid URL, show homepage
        this.uiManager.showHomepage();
      }
      
      console.log('Game application initialized successfully');
      
    } catch (error) {
      console.error('Failed to initialize game application:', error);
      this.uiManager.showError('Failed to initialize application');
    }
  }

  /**
   * Handle create game request from UI
   * Requirements: 1.2
   */
  private async handleCreateGame(): Promise<string> {
    try {
      // Initialize game client if not already done
      if (!this.gameClient) {
        this.initializeGameClient();
      }

      // Create a new lobby
      const lobbyId = await this.gameClient!.createLobby();
      this.currentLobbyId = lobbyId;
      
      // Connect to the lobby as host
      await this.gameClient!.connect(lobbyId);
      
      // Host must also join the lobby to receive updates
      await this.gameClient!.joinLobby('Host');
      
      console.log('Created and connected to lobby:', lobbyId);
      return lobbyId;
      
    } catch (error) {
      console.error('Failed to create game:', error);
      throw new Error('Failed to create game lobby');
    }
  }

  /**
   * Handle join lobby request from UI
   * Requirements: 2.1, 2.3
   */
  private async handleJoinLobby(pseudonym: string): Promise<void> {
    try {
      if (!this.currentLobbyId) {
        throw new Error('No lobby ID available');
      }

      // Initialize game client if not already done
      if (!this.gameClient) {
        this.initializeGameClient();
      }

      // Connect to the lobby
      await this.gameClient!.connect(this.currentLobbyId);
      
      // Join with pseudonym
      await this.gameClient!.joinLobby(pseudonym);
      
      console.log('Joined lobby with pseudonym:', pseudonym);
      
    } catch (error) {
      console.error('Failed to join lobby:', error);
      throw new Error('Failed to join lobby');
    }
  }

  /**
   * Handle start game request from UI
   */
  private async handleStartGame(): Promise<void> {
    try {
      if (!this.gameClient) {
        throw new Error('Game client not initialized');
      }

      await this.gameClient.startGame();
      console.log('Game start requested');
      
    } catch (error) {
      console.error('Failed to start game:', error);
      throw new Error('Failed to start game');
    }
  }

  /**
   * Handle play again request from UI
   */
  private async handlePlayAgain(): Promise<void> {
    try {
      if (!this.gameClient) {
        throw new Error('Game client not initialized');
      }

      // Reset game state and return to lobby
      this.isGameActive = false;
      
      // The lobby should still be active, just need to reset game state
      console.log('Returning to lobby for new game');
      
    } catch (error) {
      console.error('Failed to play again:', error);
      throw new Error('Failed to start new game');
    }
  }

  /**
   * Handle leave lobby request from UI
   */
  private async handleLeaveLobby(): Promise<void> {
    try {
      if (this.gameClient) {
        this.gameClient.disconnect();
      }
      
      this.cleanup();
      
      // Navigate to homepage
      window.history.pushState({}, '', '/');
      
      console.log('Left lobby');
      
    } catch (error) {
      console.error('Failed to leave lobby:', error);
      // Continue with cleanup anyway
      this.cleanup();
    }
  }

  /**
   * Initialize game client with callbacks
   */
  private initializeGameClient(): void {
    const clientCallbacks: GameClientCallbacks = {
      onStateUpdate: this.handleStateUpdate.bind(this),
      onPlayerJoined: this.handlePlayerJoined.bind(this),
      onPlayerLeft: this.handlePlayerLeft.bind(this),
      onGameStarted: this.handleGameStarted.bind(this),
      onGameEnded: this.handleGameEnded.bind(this),
      onPlayerEliminated: this.handlePlayerEliminated.bind(this),
      onError: this.handleError.bind(this),
      onConnected: this.handleConnected.bind(this),
      onDisconnected: this.handleDisconnected.bind(this)
    };

    this.gameClient = new GameClient(clientCallbacks);

    // Initialize input handler with all input callbacks
    this.inputHandler = new InputHandler({
      onJumpInput: this.handleJumpInput.bind(this),
      onMoveLeftInput: this.handleMoveLeftInput.bind(this),
      onMoveRightInput: this.handleMoveRightInput.bind(this)
    });

    // Initialize game renderer
    this.gameRenderer = new GameRenderer();
  }

  /**
   * Handles state updates from the server
   */
  private handleStateUpdate(state: StateSnapshot): void {
    // Update game renderer with authoritative server state
    // Requirements: 8.2 - Display all active players and obstacles at authoritative positions
    if (this.gameRenderer?.isReady()) {
      this.gameRenderer.updateState(state);
    }
  }

  /**
   * Handles player joined events
   */
  private handlePlayerJoined(player: any, playerCount: number): void {
    console.log('Player joined:', player.pseudonym, 'Total players:', playerCount);
    
    // If this is our own join confirmation, set the player ID in the input handler
    if (player.id === this.gameClient?.getPlayerId()) {
      this.inputHandler?.setPlayerId(player.id);
    }
    
    // Update UI with player info
    const playerInfo: PlayerInfo = {
      id: player.id,
      pseudonym: player.pseudonym,
      isHost: player.isHost || false
    };
    
    this.uiManager.addOrUpdatePlayer(playerInfo);
  }

  /**
   * Handles player left events
   */
  private handlePlayerLeft(playerId: string, playerCount: number): void {
    console.log('Player left:', playerId, 'Remaining players:', playerCount);
    this.uiManager.removePlayer(playerId);
  }

  /**
   * Handles game started events
   */
  private handleGameStarted(startTime: number, players: any[]): void {
    console.log('Game started at:', startTime, 'with players:', players.length);
    this.isGameActive = true;
    
    // Initialize game renderer if not already done
    if (!this.gameRenderer?.isReady()) {
      this.gameRenderer?.initialize({
        canvasId: 'game-canvas',
        width: 800,
        height: 600
      });
    }
    
    // Bind input controls
    this.inputHandler?.bindControls();
    
    // Show game screen
    this.uiManager.showGame();
    this.uiManager.showStatus('Game started! Jump to survive!', 'success');
  }

  /**
   * Handles game ended events
   */
  private handleGameEnded(winnerId: string | null, finalState: StateSnapshot, scoreboard: Record<string, number>): void {
    console.log('Game ended. Winner:', winnerId, 'Scoreboard:', scoreboard);
    this.isGameActive = false;
    
    // Unbind input controls
    this.inputHandler?.unbindControls();
    
    // Create final standings from final state
    const finalStandings: PlayerInfo[] = finalState.players.map((player, index) => ({
      id: player.id,
      pseudonym: player.pseudonym,
      isAlive: player.isAlive
    })).sort((a, b) => {
      // Winner first, then by elimination order (alive players first)
      if (a.isAlive && !b.isAlive) return -1;
      if (!a.isAlive && b.isAlive) return 1;
      return 0;
    });
    
    // Show results screen - Requirements: 7.4, 7.5
    this.uiManager.showResults(winnerId, finalStandings, scoreboard);
  }

  /**
   * Handles player eliminated events
   */
  private handlePlayerEliminated(playerId: string, reason: string): void {
    console.log('Player eliminated:', playerId, 'Reason:', reason);
    
    // Check if it's our player
    if (playerId === this.gameClient?.getPlayerId()) {
      this.uiManager.showStatus('You were eliminated!', 'warning');
    }
  }

  /**
   * Handles error events
   */
  private handleError(code: string, message: string): void {
    console.error('Server error:', code, message);
    this.uiManager.showError(`Server error: ${message}`);
  }

  /**
   * Handles connection established
   */
  private handleConnected(): void {
    console.log('Connected to server');
    this.uiManager.showStatus('Connected to server', 'success');
  }

  /**
   * Handles connection lost
   */
  private handleDisconnected(): void {
    console.log('Disconnected from server');
    this.uiManager.showStatus('Disconnected from server', 'error');
  }

  /**
   * Handles jump input from user
   */
  private handleJumpInput(inputEvent: InputEvent): void {
    if (this.isGameActive && this.gameClient?.isConnectionActive()) {
      this.gameClient.sendInputEvent(inputEvent);
    }
  }

  /**
   * Handles move left input from user
   */
  private handleMoveLeftInput(inputEvent: InputEvent): void {
    if (this.isGameActive && this.gameClient?.isConnectionActive()) {
      this.gameClient.sendInputEvent(inputEvent);
    }
  }

  /**
   * Handles move right input from user
   */
  private handleMoveRightInput(inputEvent: InputEvent): void {
    if (this.isGameActive && this.gameClient?.isConnectionActive()) {
      this.gameClient.sendInputEvent(inputEvent);
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.inputHandler?.unbindControls();
    this.gameRenderer?.destroy();
    this.gameClient = null;
    this.inputHandler = null;
    this.gameRenderer = null;
    this.isGameActive = false;
    this.currentLobbyId = null;
  }

  /**
   * Cleanup when the application is destroyed
   */
  destroy(): void {
    this.cleanup();
  }
}

// Initialize the game application when the page loads
let gameApp: GameApp;

window.addEventListener('DOMContentLoaded', async () => {
  gameApp = new GameApp();
  await gameApp.initialize();
  
  // Expose gameApp globally for debugging
  (window as any).gameApp = gameApp;
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (gameApp) {
    gameApp.destroy();
  }
});

// Handle browser navigation
window.addEventListener('popstate', async () => {
  if (gameApp) {
    await gameApp.initialize();
  }
});